import { switchboardKvFieldToVolts } from "@/libs/units/voltage";
import {
  Severity,
  valuesExist,
  addStackPrefixToMessage,
  type ValidationRuleGenerator
} from "../scenarioValidator";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { DEFAULT_MOTOR_FREQUENCY_HZ } from "@/libs/calculations/constants";

export const surfaceDriveChecksNames = [
  "total_voltage",
  "vsd_freq_low",
  "vsd_freq_high",
  "vsd_kva_rating",
  "vsd_amp_rating",
  "vsd_sine_wave",
  "switchboard_freq",
  "switchboard_volt_rating",
  "switchboard_amp_rating",
  "perf_depth_warning",
  "datum_casing_length_warning",
  "perf_tvd_warning"
] as const;

export const surfaceDriveChecks: ValidationRuleGenerator = ({
  calcs,
  equipment,
  wellProperties,
  parameters,
  stack
}) => [
  {
    name: "vsd_freq_low",
    message: addStackPrefixToMessage(
      `Pump Hz below Surface drive Min (${parameters.value?.minimum_frequency}) Hz`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      Boolean(equipment.value?.use_vsd) &&
      valuesExist([parameters.value?.pump_hz]),
    isInvalid: parameters.value?.pump_hz < parameters.value?.minimum_frequency,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "vsd_freq_high",
    message: addStackPrefixToMessage(
      `Pump Hz above Surface drive Max (${parameters.value?.maximum_frequency}) Hz`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      Boolean(equipment.value?.use_vsd) &&
      valuesExist([parameters.value?.pump_hz]),
    isInvalid: parameters.value?.pump_hz > parameters.value?.maximum_frequency,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },

  {
    name: "vsd_kva_rating",
    message: addStackPrefixToMessage(
      `kVA is above VSD rating`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      Boolean(equipment.value?.use_vsd) &&
      valuesExist([
        calcs.surfaceDriveCalculations.requiredKVA,
        equipment.value?.vsd_id
      ]),
    isInvalid:
      calcs.surfaceDriveCalculations.requiredKVA.value >
      (equipment.value?.r_vsd?.kva || 0),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "vsd_amp_rating",
    message: addStackPrefixToMessage(
      `Current is above VSD rating`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      Boolean(equipment.value?.use_vsd) &&
      valuesExist([
        calcs.motorCalculations.motorCurrentA,
        equipment.value?.vsd_id
      ]),
    isInvalid:
      calcs.motorCalculations.motorCurrentA.value >
      (equipment.value?.r_vsd?.max_amps || 0),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "vsd_sine_wave",
    message: addStackPrefixToMessage(
      `Alert: Requires Sine Wave Filter`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      Boolean(equipment.value?.use_vsd) &&
      valuesExist([equipment.value?.vsd_id]),
    isInvalid: equipment.value?.r_vsd?.part_number === "HAL",
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },

  {
    name: "switchboard_volt_rating",
    message: addStackPrefixToMessage(
      `Switchboard is not rated for this load`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      !equipment.value?.use_vsd &&
      valuesExist([
        calcs.surfaceDriveCalculations.requiredVoltageV,
        equipment.value?.switchboard_id
      ]),
    isInvalid:
      Math.max(
        calcs.surfaceDriveCalculations.requiredVoltageV.value || 0,
        calcs.motorCalculations.reratedNameplateV.value ||
          calcs.motorCalculations.voltageNameplate.value ||
          0
      ) > switchboardKvFieldToVolts(Number(equipment.value?.r_switchboard?.kv)),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },
  {
    name: "switchboard_amp_rating",
    message: addStackPrefixToMessage(
      `Switchboard is not rated for this current`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      !equipment.value?.use_vsd &&
      valuesExist([
        calcs.motorCalculations.motorCurrentA,
        equipment.value?.switchboard_id
      ]),
    isInvalid:
      calcs.motorCalculations.motorCurrentA.value >
      (equipment.value?.r_switchboard?.amps || 0),
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },

  {
    name: "switchboard_freq",
    message: addStackPrefixToMessage(
      `Pump Hz must be ${DEFAULT_MOTOR_FREQUENCY_HZ} Hz if using switchboard`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive:
      !equipment.value?.use_vsd &&
      valuesExist([parameters.value?.pump_hz, equipment.value?.switchboard_id]),
    isInvalid: parameters.value?.pump_hz !== DEFAULT_MOTOR_FREQUENCY_HZ,
    rulePlacement: EquipmentCalculatorSectionKey.SURFACE_DRIVE
  },

  // Bottom of equipment > Top Perf
  {
    name: "perf_depth_warning",
    message: addStackPrefixToMessage(
      `Bottom of equipment MD > Top Perf MD`,
      stack,
      equipment
    ),
    severity: Severity.warn,
    isActive: valuesExist([
      calcs.equipmentLengths.bottomOfEquipmentDepthFt,
      wellProperties.value?.top_of_perforations_measured_depth_feet
    ]),
    isInvalid:
      calcs.equipmentLengths.bottomOfEquipmentDepthFt.value >
      wellProperties.value?.top_of_perforations_measured_depth_feet,
    rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
  },

  // Datum > Casing Length
  {
    name: "datum_casing_length_warning",
    message: addStackPrefixToMessage(
      `Datum MD > Casing Length`,
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      wellProperties.value?.top_casing_id,
      wellProperties.value?.well_test_datum_point_measured_depth_feet
    ]),
    isInvalid:
      wellProperties.value?.well_test_datum_point_measured_depth_feet >
      calcs.equipmentLengths.totalCasingLengthFt.value,
    rulePlacement: EquipmentCalculatorSectionKey.WELL_TEST
  },
  {
    name: "perf_tvd_warning",
    message: addStackPrefixToMessage(
      "Top of Perfs MD must be greater than TVD",
      stack,
      equipment
    ),
    severity: Severity.error,
    isActive: valuesExist([
      wellProperties.value?.top_of_perforations_true_vertical_depth_feet,
      wellProperties.value?.top_of_perforations_measured_depth_feet
    ]),
    isInvalid:
      wellProperties.value?.top_of_perforations_true_vertical_depth_feet >
      wellProperties.value?.top_of_perforations_measured_depth_feet,
    rulePlacement: EquipmentCalculatorSectionKey.WELL_COMPLETION
  }
];
